import React, { useEffect, useState } from 'react';
import Modal from '@/components/ui/Modal';
import { Button, useToast } from '@/components/ui';
import CronScheduler from '@/components/ui/CronScheduler';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { campaignsService, type Campaign, type ScheduleAiCallRequest } from '@/services/api/campaigns';
import { ChevronDown, Phone } from 'lucide-react';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  campaign: Campaign | null;
}

const DEFAULT_CRON = '0 9 * * *';

const ScheduleAiCallModal: React.FC<Props> = ({ isOpen, onClose, campaign }) => {
  const { success, error: toastError } = useToast();
  const [agents, setAgents] = useState<{ uuid: string; display_name: string }[]>([]);

  useEffect(() => {
    if (isOpen) {
      campaignsService.getAvailableAgents().then((res: any) => {
        setAgents(res?.attributes?.callingAgents ?? []);
      });
    }
  }, [isOpen]);

  const formik = useFormik<Omit<ScheduleAiCallRequest, 'campaign_uuid'>>({
    initialValues: {
      agent_id: campaign?.assigned_agent_ids?.[0] ?? '',
      scheduled_at: campaign?.scheduled_at ?? DEFAULT_CRON,
      notes: '',
    },
    validationSchema: Yup.object({
      agent_id: Yup.string().required('Please select an AI agent'),
      scheduled_at: Yup.string().required('Please select a schedule time'),
    }),
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      if (!campaign) return;
      try {
        await campaignsService.scheduleAiCall({
          campaign_uuid: campaign.uuid,
          ...values,
        });
        success(`AI call scheduled for "${campaign.name}" successfully!`);
        resetForm();
        onClose();
      } catch (e: any) {
        toastError(e?.message || 'Failed to schedule AI call');
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    if (!isOpen) return;
    formik.resetForm({
      values: {
        agent_id: campaign?.assigned_agent_ids?.[0] ?? '',
        scheduled_at: campaign?.scheduled_at ?? DEFAULT_CRON,
        notes: '',
      },
    });
  }, [isOpen, campaign]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Schedule AI Agent Call"
      size="md"
      footer={
        <div className="flex justify-end gap-3 px-2">
          <Button
            type="submit"
            form="schedule-ai-call-form"
            disabled={formik.isSubmitting}
            className="bg-primary-button hover:bg-primary-button text-white flex items-center gap-2"
          >
            <Phone size={15} />
            {formik.isSubmitting ? 'Scheduling…' : 'Schedule Call'}
          </Button>
          <Button variant="outline" type="button" onClick={onClose}>
            Cancel
          </Button>
        </div>
      }
    >
      <form id="schedule-ai-call-form" onSubmit={formik.handleSubmit} className="px-4 py-4 space-y-5">
        {/* Campaign info banner */}
        {campaign && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg px-4 py-3 text-sm">
            <p className="text-xs text-indigo-500 font-medium uppercase tracking-wide mb-0.5">Campaign</p>
            <p className="font-semibold text-indigo-900">{campaign.name}</p>
            {campaign.description && (
              <p className="text-indigo-600 text-xs mt-0.5">{campaign.description}</p>
            )}
          </div>
        )}

        {/* Agent select */}
        <div>
          <label className="text-xs font-medium mb-1 block">AI Agent *</label>
          <div className="relative">
            <select
              name="agent_id"
              value={formik.values.agent_id}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="w-full h-10 border border-gray-300 rounded-lg px-3 py-2 text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">Select an agent…</option>
              {agents.map((a) => (
                <option key={a.uuid} value={a.uuid}>
                  {a.display_name}
                </option>
              ))}
            </select>
            <ChevronDown size={14} className="absolute right-3 top-3 text-gray-400 pointer-events-none" />
          </div>
          {formik.touched.agent_id && formik.errors.agent_id && (
            <p className="text-red-500 text-xs mt-1">{formik.errors.agent_id}</p>
          )}
        </div>

        {/* Scheduled At */}
        <div>
          <CronScheduler
            label="Schedule Date & Time *"
            value={formik.values.scheduled_at}
            onChange={(cron) => formik.setFieldValue('scheduled_at', cron)}
          />
          {formik.touched.scheduled_at && formik.errors.scheduled_at && (
            <p className="text-red-500 text-xs mt-1">{formik.errors.scheduled_at}</p>
          )}
        </div>

        {/* Notes */}
        <div>
          <label className="text-xs font-medium mb-1 block">Notes (optional)</label>
          <textarea
            name="notes"
            value={formik.values.notes ?? ''}
            onChange={formik.handleChange}
            rows={3}
            placeholder="Any additional instructions for the AI agent…"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
          />
        </div>
      </form>
    </Modal>
  );
};

export default ScheduleAiCallModal;
